import { type OrgEventAutomationRule } from 'hooks/orgEvents/orgEventAutomationHooks';
import { describeRuleConditions } from './automationRuleLogic';
import { checkFrequencyLabel } from './ruleFormModel';

export const NO_CONDITIONS_TRIGGER = 'No conditions yet';
const NO_EVENT_TYPE = '—';

/** Display-ready projection of a rule, one per row of the Event Settings table. */
export type AutomationRuleRow = {
  id: string;
  name: string;
  enabled: boolean;
  updatedAt: string;
  trigger: string;
  eventType: string;
  checkFrequency: string;
  hasNotification: boolean;
  hasChecklist: boolean;
};

export function toAutomationRuleRow(
  rule: OrgEventAutomationRule
): AutomationRuleRow {
  const trigger = describeRuleConditions({
    logic: rule.logic,
    source: rule.source,
    conditions: rule.conditions,
    logicBlocks: rule.logic_blocks ?? [],
  });
  return {
    id: rule.id,
    name: rule.name,
    enabled: Boolean(rule.enabled),
    updatedAt: rule.updated_at,
    trigger: trigger || NO_CONDITIONS_TRIGGER,
    eventType: rule.event_template?.type?.trim() || NO_EVENT_TYPE,
    checkFrequency: checkFrequencyLabel(rule.repeat_pattern),
    hasNotification: Boolean(rule.notify_on_create),
    hasChecklist: Boolean(rule.checklist_on_create),
  };
}

/**
 * The server orders by `created_at`, which would disagree with the Last Updated
 * column, so sort here instead.
 */
export function sortAutomationRuleRows(
  rows: AutomationRuleRow[]
): AutomationRuleRow[] {
  // An unparseable timestamp sorts last rather than poisoning the comparator.
  const time = (value: string): number => {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? -Infinity : parsed;
  };
  return [...rows].sort((a, b) => time(b.updatedAt) - time(a.updatedAt));
}

/**
 * Matches on everything a row shows, plus the keywords behind the two icon-only
 * columns, so typing "checklist" surfaces exactly the rules carrying one.
 */
export function filterAutomationRuleRows(
  rows: AutomationRuleRow[],
  search: string
): AutomationRuleRow[] {
  const term = search.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter((row) =>
    [
      row.name,
      row.trigger,
      row.eventType,
      row.checkFrequency,
      row.enabled ? 'Enabled' : 'Disabled',
      row.hasNotification ? 'Notification' : '',
      row.hasChecklist ? 'Response Checklist' : '',
    ]
      .join(' ')
      .toLowerCase()
      .includes(term)
  );
}
